import {
  closeBranchesForJoin,
  ConductorTask,
  createForkJoinTask,
  createJoinTask,
  getTaskReferenceName
} from 'Automations/nodeDefinitions/conductor'

type SupportingTaskName = 'supportingFork' | 'supportingJoin'

const taskReferenceBuilder = getTaskReferenceName<SupportingTaskName>('SWITCH')

/**
 * Wraps a condition node's supporting branches so they run concurrently before
 * the node is evaluated. Each becomes one arm of the fork, so the added latency
 * is the slowest branch rather than their sum.
 *
 *   fork -+-> getQuoteTemplate_tpl-a -> compareQuotePricing_tpl-a -+-> join
 *         +-> getQuoteTemplate_tpl-b ---------------------------- -+
 *
 * The join waits on each branch's last task, which is what keeps a branch
 * serial: a later task in it reads an earlier one's output.
 *
 * A lone branch is returned bare: a single-arm fork buys no parallelism and
 * costs two extra Conductor tasks.
 */
export const wrapSupportingBranchesInFork = ({
  nodeId,
  branches
}: {
  nodeId: string
  branches: ConductorTask[][]
}): ConductorTask[] => {
  // Only the forked path joins, so a lone branch keeps its own shape: tasks
  // after a switch already run after its chosen case.
  if (branches.length < 2) return branches.flat()

  const { branches: joinableBranches, joinOn } = closeBranchesForJoin(branches)

  const taskReferenceName = taskReferenceBuilder(nodeId)

  return [
    createForkJoinTask({
      name: 'supporting-fork',
      taskReferenceName: taskReferenceName('supportingFork'),
      forkTasks: joinableBranches,
      inputParameters: {}
    }),
    createJoinTask({
      name: 'supporting-join',
      taskReferenceName: taskReferenceName('supportingJoin'),
      joinOn,
      inputParameters: {}
    })
  ]
}
